// /phototracker (also served at /intersections) — password-gated field tool for
// photographing campus, ported from UMapsTools' Campus Editor.
//
// The field flow is building-first: pick a building, pick a floor, and the map
// draws that floor's structure polygons, nav edges and *every* nav node. Walk
// the floor, take a photo, tap the node you shot — the run records the node id,
// so the photos can be matched back to nodes by a script afterwards (nothing
// here cares whether a node is a coded intersection or a plain waypoint).
//
// PLACES (study spots, restaurants, libraries, clubs, offices, bathrooms from
// places.json) work differently: a place usually needs several photos, so a
// place can be tapped repeatedly and collects an ordered list of photo slots.
//
// A SESSION is one photo run: the ordered list of everything you shot, so a
// batch of photos can be matched to nodes/places by position. Sessions save to
// the server (/api/phototracker) — press Save, or let the autosave catch it —
// and are mirrored to localStorage so a dead signal never loses the run. The
// dashboard lists past runs to reopen, download or delete, and starts new ones.
//
// Targets and per-building geometry come from data/phototracker/, generated by
// UMapsTools' scripts/export-phototracker-web.py. Read-only on that data:
// creating nodes/places writes files in the UMapsTools repo, not here.
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import UMapsWordmark from './components/UMapsWordmark.jsx';

const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;
// In dev the API runs on the Express server (port 3001); in prod it's same-origin.
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const TOKEN_KEY = 'umaps-admin-token';
const SESSION_KEY = 'umaps-pt-session';     // local mirror of the run in progress
const AUTOSAVE_MS = 15000;

const CENTER = { lat: 49.80939016935401, lng: -97.1343494222327 };
const TARGET_ZOOM = 20;
const DONE_COLOR = '#2e7d32';
const SEL_COLOR = '#ffca28';

// Nav node types (nav_<slug>.json `type`), and place types (places.json).
const NODE_COLOR = {
  intersection: '#39ff14', waypoint: '#1a73e8', door: '#8d6e63', stair: '#c4956a',
  elevator: '#78909c', exit: '#e53935', parking: '#43a047', 'non-accessible': '#212121',
};
const NODE_LABEL = {
  intersection: 'Intersections', waypoint: 'Waypoints', door: 'Doors', stair: 'Stairs',
  elevator: 'Elevators', exit: 'Exits', parking: 'Parking', 'non-accessible': 'Non-accessible',
};
const NODE_ONE = {
  intersection: 'Intersection', waypoint: 'Waypoint', door: 'Door', stair: 'Stair',
  elevator: 'Elevator', exit: 'Exit', parking: 'Parking', 'non-accessible': 'Non-accessible',
};
const NODE_ICON = {
  intersection: '🔀', waypoint: '📍', door: '🚪', stair: '🪜',
  elevator: '🛗', exit: '🏃', parking: '🅿️', 'non-accessible': '⛔',
};
const KIND_COLOR = {
  study: '#3949ab', library: '#6d4c41', restaurant: '#e64a19',
  bathroom: '#00838f', club: '#8e24aa', office: '#455a64',
};
const KIND_LABEL = {
  study: 'Study spots', library: 'Libraries', restaurant: 'Restaurants',
  bathroom: 'Bathrooms', club: 'Clubs', office: 'Offices',
};
const KIND_ICON = {
  study: '📚', library: '🏛', restaurant: '🍽',
  bathroom: '🚻', club: '🎉', office: '🏢',
};
const colorFor = (e) => NODE_COLOR[e] || KIND_COLOR[e] || '#1a73e8';
const iconFor = (e) => NODE_ICON[e] || KIND_ICON[e] || '📍';

// Structure polygons — the shared map scheme (UMAPS_BRANDING_GUIDE.md §5).
const POLY_OPTS = {
  outline:  { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 2   },
  room:     { fill: '#FCF8EF', fillOp: 0.8,  stroke: '#dbdbdb', strokeW: 1.5 },
  stair:    { fill: '#c4956a', fillOp: 0.85, stroke: '#a0724a', strokeW: 1.5 },
  elevator: { fill: '#b0bec5', fillOp: 0.85, stroke: '#78909c', strokeW: 1.5 },
  blocked:  { fill: '#000000', fillOp: 0.85, stroke: '#000000', strokeW: 1.5 },
  tunnel:   { fill: '#efebe9', fillOp: 0.9,  stroke: '#6d4c41', strokeW: 2   },
};
const styleFor = t => POLY_OPTS[t] || POLY_OPTS.room;
const EDGE_COLORS = ['#1a73e8', '#6d4c41', '#ff9800']; // indoor / tunnel / outdoor
const floorTag = f => (f === -1 ? 'T' : f === 0 ? 'O' : String(f));

const nowISO = () => new Date().toISOString();
const shortTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
const shortDate = (iso) => { try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };

// Node records are stored compactly: i=id f=floor t=type la/ln=coords
// r=room_number l=label c=intersection code p=has panorama.
const idTail = (id) => String(id).split('_').pop();
const nodeName = (n) => (n.c ? `Intersection ${n.c}` : n.l ? n.l
  : `${NODE_ONE[n.t] || n.t} ${n.r ? n.r : `·${idTail(n.i)}`}`);

// Sessions written before nodes existed only ever held intersections + places.
const entryType = (e) => e.type || (e.kind === 'intersection' ? 'node' : 'place');

const newSession = () => ({
  id: `run_${Date.now().toString(36)}`,
  name: `Run — ${shortDate(nowISO())}`,
  startedAt: nowISO(),
  entries: [],
});

function loadLocalSession() {
  try {
    const v = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    return v && v.id && Array.isArray(v.entries) ? v : null;
  } catch { return null; }
}

export default function PhotoTrackerPage() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const [view, setView] = useState('track');           // 'track' | 'dash'
  const [index, setIndex] = useState(null);            // { buildings, targets }
  const [session, setSession] = useState(() => loadLocalSession() || newSession());
  const [sessions, setSessions] = useState([]);
  const [storage, setStorage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [note, setNote] = useState('');

  const [slug, setSlug] = useState('');
  const [floor, setFloor] = useState(1);
  const [tab, setTab] = useState('nodes');             // 'nodes' | 'places' | 'shots'
  const [selId, setSelId] = useState(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(true);          // tap-a-marker-to-shoot mode
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());

  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY || '' });
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const ctxRef = useRef(new Map());                     // slug → { rooms, edges, nodes }
  const [ctxTick, setCtxTick] = useState(0);
  const polyObjsRef = useRef([]);
  const lineObjsRef = useRef([]);
  const markerObjsRef = useRef([]);                     // [{ m, id, kind, place }]
  const fittedRef = useRef(null);                       // slug the map has framed
  // Read inside the marker click handlers so flipping the mode — or shooting a
  // node — never has to tear down and rebuild the floor's markers.
  const activeRef = useRef(active); useEffect(() => { activeRef.current = active; }, [active]);

  const saveToken = (t) => {
    setToken(t);
    try { t ? sessionStorage.setItem(TOKEN_KEY, t) : sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  };

  const login = async (e) => {
    e?.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.token) { setErr(j.error || 'Login failed.'); setBusy(false); return; }
      saveToken(j.token); setPw('');
    } catch { setErr('Network error.'); }
    setBusy(false);
  };

  const api = useCallback(async (qs = '', body = null) => {
    const r = await fetch(`${API_BASE}/api/phototracker${qs}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (r.status === 401) { saveToken(''); throw new Error('Session expired — log in again.'); }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
    return j;
  }, [token]);

  // ── Targets + past sessions ────────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setIndex(null); return; }
    let cancelled = false;
    api().then(d => { if (!cancelled) setIndex(d); }).catch(e => { if (!cancelled) setErr(e.message); });
    api('?sessions=1').then(d => {
      if (cancelled) return;
      setSessions(d.sessions || []); setStorage(d.storage || '');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, api]);

  // Local mirror: the run survives a reload, a crash or no signal.
  useEffect(() => { try { localStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* full */ } }, [session]);

  const buildings = useMemo(() => index?.buildings || [], [index]);
  const nameOf = useCallback((s) => buildings.find(b => b.slug === s)?.name || s, [buildings]);
  // index.json's targets carry the places; nodes come from the building file.
  const places = useMemo(() => (index?.targets || []).filter(t => t.kind !== 'intersection'), [index]);

  // id → the run positions it was shot at (a place can hold several).
  const shotsById = useMemo(() => {
    const m = new Map();
    session.entries.forEach((e, i) => {
      const a = m.get(e.targetId) || [];
      a.push(i);
      m.set(e.targetId, a);
    });
    return m;
  }, [session.entries]);

  // ── Saving ─────────────────────────────────────────────────────────────────
  const sessionRef = useRef(session); useEffect(() => { sessionRef.current = session; }, [session]);
  const save = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      const d = await api('', { session: sessionRef.current });
      setDirty(false); setSavedAt(Date.now());
      if (d.storage) setStorage(d.storage);
      setSessions(prev => {
        const rest = prev.filter(s => s.id !== d.session.id);
        return [d.session, ...rest];
      });
      if (!silent) setNote('Saved.');
    } catch (e) {
      setNote(e.message);
    }
    setSaving(false);
  }, [api]);

  // Autosave a dirty run, and again when the tab goes to the background.
  useEffect(() => {
    if (!dirty || !token) return;
    const t = setTimeout(() => save(true), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [dirty, token, save, session]);
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden' && dirty) save(true); };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [dirty, save]);

  const buzz = () => { try { navigator.vibrate?.(15); } catch { /* unsupported */ } };

  // A node is one photo: tap marks it, tap again undoes it.
  const toggleNode = useCallback((n, forSlug) => {
    setSession(s => {
      const has = s.entries.some(e => e.targetId === n.i);
      const entries = has
        ? s.entries.filter(e => e.targetId !== n.i)
        : [...s.entries, { targetId: n.i, type: 'node', kind: n.t, code: n.c || '',
                           name: nodeName(n), slug: forSlug, floor: n.f,
                           lat: n.la, lng: n.ln, at: nowISO() }];
      return { ...s, entries };
    });
    setDirty(true); buzz();
  }, []);

  // A place holds as many photos as you take of it — each tap appends one.
  const addPlacePhoto = useCallback((t) => {
    setSession(s => ({ ...s, entries: [...s.entries, {
      targetId: t.id, type: 'place', kind: t.kind, code: t.code || '', name: t.name,
      slug: t.slug, floor: t.floor, lat: t.lat, lng: t.lng, at: nowISO(),
    }] }));
    setDirty(true); buzz();
  }, []);

  const removeShot = useCallback((i) => {
    setSession(s => ({ ...s, entries: s.entries.filter((_, k) => k !== i) }));
    setDirty(true);
  }, []);

  const startNew = useCallback(async () => {
    if (session.entries.length && dirty) await save(true);
    if (session.entries.length && !window.confirm('Start a new run? The current one stays saved in the dashboard.')) return;
    setSession(newSession()); setDirty(false); setSavedAt(null);
    setNote('New run started.'); setView('track');
  }, [session.entries.length, dirty, save]);

  const openSession = useCallback(async (s) => {
    if (dirty) await save(true);
    setSession({ id: s.id, name: s.name, startedAt: s.startedAt, entries: s.entries || [] });
    setDirty(false); setSavedAt(null); setView('track');
    setNote(`Opened "${s.name}".`);
  }, [dirty, save]);

  const removeSession = useCallback(async (s) => {
    if (!window.confirm(`Delete "${s.name}" (${(s.entries || []).length} photos)?`)) return;
    try {
      const d = await api('', { deleteId: s.id });
      setSessions(d.sessions || []);
      setNote('Run deleted.');
    } catch (e) { setNote(e.message); }
  }, [api]);

  // The download is the hand-off to the assignment script: node id or place id
  // per photo, in the order the photos were taken. `photoOfTarget` numbers the
  // shots within one place (1, 2, 3…) so a place's images stay ordered.
  const download = useCallback((s) => {
    const seen = new Map();
    const rows = (s.entries || []).map((e, i) => {
      const k = (seen.get(e.targetId) || 0) + 1;
      seen.set(e.targetId, k);
      const type = entryType(e);
      return {
        order: i + 1, type,
        nodeId: type === 'node' ? e.targetId : undefined,
        placeId: type === 'place' ? e.targetId : undefined,
        photoOfTarget: k, kind: e.kind, code: e.code, name: e.name,
        building: e.slug, buildingName: nameOf(e.slug), floor: e.floor,
        lat: e.lat, lng: e.lng, markedAt: e.at,
      };
    });
    const doc = { session: s.name, startedAt: s.startedAt, photos: rows.length, order: rows };
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(s.name).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [nameOf]);

  // ── Map ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded || !token || view !== 'track' || !mapRef.current || mapInstanceRef.current) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: CENTER, zoom: 16, mapTypeId: 'satellite',
      tilt: 0, heading: 0, rotateControl: false,
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      clickableIcons: false, gestureHandling: 'greedy',
    });
    setMapReady(true);
  }, [isLoaded, token, view]);

  useEffect(() => {
    if (!slug || !token || ctxRef.current.has(slug)) return;
    let cancelled = false;
    ctxRef.current.set(slug, { rooms: [], edges: [], nodes: [] });
    api(`?building=${encodeURIComponent(slug)}`)
      .then(d => { if (!cancelled) { ctxRef.current.set(slug, d); setCtxTick(t => t + 1); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug, token, api]);

  // Re-read on ctxTick: the fetch above stores into a ref, so the tick is what
  // tells React the building's geometry has landed.
  const ctx = (slug && ctxRef.current.get(slug)) || null;
  const allNodes = useMemo(() => ctx?.nodes || [], [ctx]);

  // Structure polygons + nav edges for the floor.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const clear = () => {
      polyObjsRef.current.forEach(o => o.setMap(null)); polyObjsRef.current = [];
      lineObjsRef.current.forEach(o => o.setMap(null)); lineObjsRef.current = [];
    };
    clear();
    const c = slug && ctxRef.current.get(slug);
    if (!c) return;
    const outlines = (c.rooms || []).filter(r => r.t === 'outline');
    let footprint = outlines.filter(r => r.f === floor);
    if (!footprint.length && outlines.length) {
      const lowest = Math.min(...outlines.map(o => o.f));
      footprint = outlines.filter(o => o.f === lowest);
    }
    for (const r of [...footprint, ...(c.rooms || []).filter(r => r.t !== 'outline' && r.f === floor)]) {
      const st = styleFor(r.t);
      polyObjsRef.current.push(new window.google.maps.Polygon({
        map, clickable: false, zIndex: r.t === 'outline' ? 1 : 2,
        paths: r.r.map(([lat, lng]) => ({ lat, lng })),
        fillColor: st.fill, fillOpacity: st.fillOp,
        strokeColor: st.stroke, strokeOpacity: 1, strokeWeight: st.strokeW,
      }));
    }
    for (const [aLat, aLng, bLat, bLng, f, kind] of c.edges || []) {
      if (f !== floor) continue;
      lineObjsRef.current.push(new window.google.maps.Polyline({
        map, clickable: false, zIndex: 200,
        strokeColor: EDGE_COLORS[kind] || EDGE_COLORS[0], strokeOpacity: 0.8, strokeWeight: 3,
        path: [{ lat: aLat, lng: aLng }, { lat: bLat, lng: bLng }],
      }));
    }
    return clear;
  }, [mapReady, slug, floor, ctxTick]);

  // Frame a newly picked building once its geometry lands.
  useEffect(() => {
    if (!mapReady || !slug || fittedRef.current === slug || !allNodes.length) return;
    const b = new window.google.maps.LatLngBounds();
    allNodes.forEach(n => b.extend({ lat: n.la, lng: n.ln }));
    mapInstanceRef.current.fitBounds(b, 32);
    fittedRef.current = slug;
  }, [mapReady, slug, allNodes]);

  // ── What the floor shows ───────────────────────────────────────────────────
  const floorNodes = useMemo(
    () => allNodes.filter(n => n.f === floor),
    [allNodes, floor]);
  const floorPlaces = useMemo(
    () => places.filter(p => p.slug === slug && p.floor === floor),
    [places, slug, floor]);

  const typeCounts = useMemo(() => {
    const c = new Map();
    for (const n of floorNodes) c.set(n.t, (c.get(n.t) || 0) + 1);
    return [...c.entries()].sort((a, b) => b[1] - a[1]);
  }, [floorNodes]);

  const shownNodes = useMemo(
    () => floorNodes.filter(n => !hiddenTypes.has(n.t)),
    [floorNodes, hiddenTypes]);

  // Nav-node + place markers for the current floor. Built once per floor: a
  // floor can carry 150 of them, so shooting one restyles it (below) instead of
  // rebuilding the lot.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    const clear = () => { markerObjsRef.current.forEach(o => o.m.setMap(null)); markerObjsRef.current = []; };
    clear();
    if (!slug) return;

    for (const n of shownNodes) {
      const m = new window.google.maps.Marker({
        map, position: { lat: n.la, lng: n.ln }, zIndex: 300,
      });
      m.addListener('click', () => {
        setSelId(n.i); setTab('nodes');
        if (activeRef.current) toggleNode(n, slug);
      });
      markerObjsRef.current.push({ m, id: n.i, kind: n.t, place: false, name: nodeName(n), code: n.c || '' });
    }
    for (const p of floorPlaces) {
      const m = new window.google.maps.Marker({
        map, position: { lat: p.lat, lng: p.lng }, zIndex: 330,
      });
      m.addListener('click', () => {
        setSelId(p.id); setTab('places');
        if (activeRef.current) addPlacePhoto(p);
      });
      markerObjsRef.current.push({ m, id: p.id, kind: p.kind, place: true, name: p.name, code: '' });
    }
    return clear;
  }, [mapReady, slug, shownNodes, floorPlaces, toggleNode, addPlacePhoto]);

  // Selection + shot state, painted onto the markers already on the map.
  useEffect(() => {
    if (!mapReady) return;
    for (const o of markerObjsRef.current) {
      const shots = shotsById.get(o.id) || [];
      const done = shots.length > 0;
      const sel = o.id === selId;
      const base = o.place ? 10 : 6;
      o.m.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: sel ? base + 3 : done ? base + 2 : base,
        fillColor: sel ? SEL_COLOR : done ? DONE_COLOR : colorFor(o.kind),
        fillOpacity: 1, strokeColor: '#1b1b1b', strokeWeight: sel ? 3 : o.place ? 2 : 1.5,
      });
      o.m.setZIndex((o.place ? 330 : 300) + (sel ? 20 : done ? 10 : 0));
      o.m.setTitle(`${o.name}${done ? ` · photo${shots.length > 1 ? 's' : ''} #${shots.map(i => i + 1).join(', #')}` : ''}`);
      // Places always show their icon; plain nav nodes stay unlabelled until
      // shot — a hundred labelled waypoints is unreadable.
      const text = o.place
        ? (done ? `${iconFor(o.kind)}${shots.length}` : iconFor(o.kind))
        : (done ? `${o.code}✓${shots[0] + 1}` : o.code);
      o.m.setLabel(text
        ? { text, fontSize: o.place ? '12px' : '11px', fontWeight: '700', color: '#12490a', className: 'ixp-maplabel' }
        : null);
    }
  }, [mapReady, selId, shotsById, shownNodes, floorPlaces]);

  const panTo = useCallback((lat, lng) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.panTo({ lat, lng });
    if ((map.getZoom() ?? 0) < TARGET_ZOOM) map.setZoom(TARGET_ZOOM);
  }, []);

  const pickBuilding = useCallback((s) => {
    setSlug(s); setSelId(null); setQuery('');
    const fl = buildings.find(b => b.slug === s)?.floors || [];
    setFloor(fl.includes(1) ? 1 : (fl[0] ?? 1));
  }, [buildings]);

  const copyId = useCallback((id) => {
    try { navigator.clipboard?.writeText(id); setNote(`Copied ${id}`); } catch { /* no clipboard */ }
  }, []);

  // ── Lists ──────────────────────────────────────────────────────────────────
  const q = query.trim().toLowerCase();
  const nodeGroups = useMemo(() => {
    const hits = shownNodes.filter(n => !q
      || [n.c, n.l, n.r, n.i, NODE_ONE[n.t]].filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q)));
    const by = new Map();
    for (const n of hits) {
      if (!by.has(n.t)) by.set(n.t, []);
      by.get(n.t).push(n);
    }
    return [...by.entries()]
      .map(([t, list]) => ({
        key: t, label: NODE_LABEL[t] || t,
        list: list.sort((a, b) => String(a.c || a.r || idTail(a.i))
          .localeCompare(String(b.c || b.r || idTail(b.i)), undefined, { numeric: true })),
      }))
      .sort((a, b) => (a.key === 'intersection' ? -1 : b.key === 'intersection' ? 1 : a.label.localeCompare(b.label)));
  }, [shownNodes, q]);

  const placeRows = useMemo(() => floorPlaces
    .filter(p => !q || [p.name, p.code, p.sub].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
    .sort((a, b) => String(a.name).localeCompare(String(b.name))), [floorPlaces, q]);

  const selPlace = useMemo(() => places.find(p => p.id === selId) || null, [places, selId]);
  const selNode = useMemo(() => allNodes.find(n => n.i === selId) || null, [allNodes, selId]);

  const toggleGroup = (key) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleType = (t) => setHiddenTypes(prev => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });
  const floorsOf = (s) => buildings.find(b => b.slug === s)?.floors || [];

  if (!token) {
    return (
      <div className="ixp-login">
        <style>{CSS}</style>
        <form className="ixp-card" onSubmit={login}>
          <div className="ixp-h1">📷 Photo Tracker</div>
          <div className="ixp-muted">Enter the admin code</div>
          {err && <div className="ixp-err">{err}</div>}
          <input className="ixp-input" type="password" autoFocus placeholder="Admin code"
            value={pw} onChange={e => setPw(e.target.value)} />
          <button className="ixp-primary" type="submit" disabled={busy || !pw}>{busy ? 'Checking…' : 'Log in'}</button>
        </form>
      </div>
    );
  }
  if (!MAPS_KEY) return <div className="ixp-login"><style>{CSS}</style><div className="ixp-card">Missing REACT_APP_MAPS_KEY.</div></div>;
  if (loadError) return <div className="ixp-login"><style>{CSS}</style><div className="ixp-card">Google Maps failed to load.</div></div>;

  const saveLabel = saving ? 'Saving…' : dirty ? 'Save' : savedAt ? `Saved ${shortTime(new Date(savedAt).toISOString())}` : 'Saved';

  return (
    <div className={`ixp-page ${view === 'dash' ? 'dash' : ''}`}>
      <style>{CSS}</style>
      <header className="ixp-head">
        <UMapsWordmark tool="Photo Tracker" size={20} />
        <span className="ixp-count">{session.entries.length} shot</span>
        <span className="ixp-spacer" />
        <button className={`ixp-btn ${dirty ? 'warn' : ''}`} onClick={() => save(false)} disabled={saving}>{saveLabel}</button>
        <button className={`ixp-btn ${view === 'dash' ? 'on' : ''}`} onClick={() => setView(v => (v === 'dash' ? 'track' : 'dash'))}>
          {view === 'dash' ? 'Back' : 'Dashboard'}
        </button>
      </header>

      {view === 'dash' ? (
        <div className="ixp-dash">
          {note && <div className="ixp-note" onClick={() => setNote('')}>{note}</div>}
          <div className="ixp-panel">
            <div className="ixp-panel-h">Current run</div>
            <input className="ixp-input ixp-namein" value={session.name}
              onChange={e => { setSession(s => ({ ...s, name: e.target.value })); setDirty(true); }} />
            <div className="ixp-panel-sub">
              Started {shortDate(session.startedAt)} · {session.entries.length} photo{session.entries.length === 1 ? '' : 's'}
              {dirty ? ' · unsaved changes' : ''}
            </div>
            <div className="ixp-panel-row">
              <button className="ixp-primary sm" onClick={() => save(false)} disabled={saving}>Save</button>
              <button className="ixp-btn" onClick={() => download(session)} disabled={!session.entries.length}>⬇ Download</button>
              <button className="ixp-btn" onClick={startNew}>＋ Start new session</button>
            </div>
            {session.entries.length > 0 && (
              <ol className="ixp-order-list">
                {session.entries.map((e, i) => (
                  <li key={e.targetId + '_' + i}>
                    <span className="ixp-oi">{i + 1}</span>
                    <span className="ixp-on">{iconFor(e.kind)} {e.name}</span>
                    <span className="ixp-ob">{nameOf(e.slug)} · F{floorTag(e.floor)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="ixp-panel">
            <div className="ixp-panel-h">Past runs {storage ? <span className="ixp-storage">saved to {storage === 'kv' ? 'the server' : 'a local file'}</span> : null}</div>
            {sessions.filter(s => s.id !== session.id).length === 0 && <div className="ixp-empty">No other runs yet.</div>}
            {sessions.filter(s => s.id !== session.id).map(s => (
              <div className="ixp-srow" key={s.id}>
                <div className="ixp-stext">
                  <div className="ixp-sname">{s.name}</div>
                  <div className="ixp-ssub">{(s.entries || []).length} photos · {shortDate(s.updatedAt || s.startedAt)}</div>
                </div>
                <button className="ixp-btn" onClick={() => openSession(s)}>Open</button>
                <button className="ixp-btn" onClick={() => download(s)}>⬇</button>
                <button className="ixp-btn danger" onClick={() => removeSession(s)}>✕</button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="ixp-pickbar">
            <select className="ixp-select" value={slug} onChange={e => pickBuilding(e.target.value)}>
              <option value="">Pick a building…</option>
              {buildings.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
            <div className="ixp-floors">
              {floorsOf(slug).map(f => (
                <button key={f} className={`ixp-floor ${f === floor ? 'on' : ''}`} onClick={() => { setFloor(f); setSelId(null); }}>{floorTag(f)}</button>
              ))}
            </div>
          </div>

          <div className={`ixp-map-wrap ${active ? 'active' : ''}`}>
            <div className="ixp-map" ref={mapRef} />
            <div className="ixp-mapctrls">
              <span className="ixp-shown">
                {!slug ? 'Pick a building to load its nodes'
                  : active ? `Tap to shoot · ${shownNodes.length} nodes` : `${shownNodes.length} nodes · F${floorTag(floor)}`}
              </span>
              <button className={`ixp-active ${active ? 'on' : ''}`} onClick={() => setActive(a => !a)}
                aria-pressed={active} title="Tap a node on the map to record a photo of it">
                {active ? '● Active' : '○ Active'}
              </button>
            </div>
          </div>

          <div className="ixp-listwrap">
            <div className="ixp-tabs">
              {[['nodes', `Nodes ${shownNodes.length}`], ['places', `Places ${floorPlaces.length}`], ['shots', `Shots ${session.entries.length}`]].map(([k, label]) => (
                <button key={k} className={`ixp-tab ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>{label}</button>
              ))}
            </div>

            {note && <div className="ixp-note" onClick={() => setNote('')}>{note}</div>}
            {err && <div className="ixp-err ixp-inline">{err}</div>}
            {!index && !err && <div className="ixp-empty">Loading targets…</div>}
            {index && !slug && <div className="ixp-empty">Pick a building above to start a floor.</div>}

            {index && slug && tab !== 'shots' && (
              <input className="ixp-search" placeholder={tab === 'nodes' ? 'Search a room, label or node id…' : 'Search a place…'}
                value={query} onChange={e => setQuery(e.target.value)} />
            )}

            {/* ── Nodes ─────────────────────────────────────────────────── */}
            {index && slug && tab === 'nodes' && (
              <>
                <div className="ixp-kinds">
                  {typeCounts.map(([t, n]) => (
                    <button key={t} className={`ixp-kind ${hiddenTypes.has(t) ? '' : 'on'}`} onClick={() => toggleType(t)}>
                      {iconFor(t)} {NODE_LABEL[t] || t} {n}
                    </button>
                  ))}
                </div>
                {selNode && (
                  <div className="ixp-detail">
                    <div className="ixp-dname">{iconFor(selNode.t)} {nodeName(selNode)}</div>
                    <button className="ixp-idchip" onClick={() => copyId(selNode.i)} title="Copy node id">{selNode.i} ⧉</button>
                    <div className="ixp-panel-row">
                      <button className={`ixp-primary sm ${shotsById.has(selNode.i) ? 'undo' : ''}`}
                        onClick={() => toggleNode(selNode, slug)}>
                        {shotsById.has(selNode.i) ? `✓ Shot #${shotsById.get(selNode.i)[0] + 1} — undo` : '📷 Mark shot'}
                      </button>
                      <button className="ixp-btn" onClick={() => panTo(selNode.la, selNode.ln)}>Centre</button>
                    </div>
                  </div>
                )}
                {nodeGroups.length === 0 && <div className="ixp-empty">No nodes on this floor match.</div>}
                {nodeGroups.map(g => {
                  const open = !!q || !collapsed.has(g.key);
                  return (
                    <div key={g.key} className="ixp-group">
                      <button className="ixp-grouphead" onClick={() => toggleGroup(g.key)} aria-expanded={open}>
                        <span className={`ixp-caret ${open ? 'open' : ''}`}>▶</span>
                        <span className="ixp-groupname">{g.label}</span>
                        <span className="ixp-groupn">{g.list.filter(n => shotsById.has(n.i)).length}/{g.list.length}</span>
                      </button>
                      {open && g.list.map(n => {
                        const shots = shotsById.get(n.i);
                        const isDone = !!shots?.length;
                        return (
                          <div key={n.i} className={`ixp-row ${n.i === selId ? 'sel' : ''} ${isDone ? 'done' : ''}`}>
                            <button className="ixp-rowmain" onClick={() => { setSelId(n.i); panTo(n.la, n.ln); }}>
                              <span className="ixp-chip" style={{ background: `${colorFor(n.t)}1f`, borderColor: `${colorFor(n.t)}55` }}>
                                {n.c || iconFor(n.t)}
                              </span>
                              <span className="ixp-rowtext">
                                <span className="ixp-rowname">{nodeName(n)}</span>
                                <span className="ixp-rowsub">{NODE_ONE[n.t] || n.t} · {idTail(n.i)}{n.p ? ' · 📷' : ''}</span>
                              </span>
                            </button>
                            {isDone && <span className="ixp-order">#{shots[0] + 1}</span>}
                            <button className={`ixp-done ${isDone ? 'on' : ''}`} onClick={() => toggleNode(n, slug)}>
                              {isDone ? 'Undo' : 'Shot'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </>
            )}

            {/* ── Places (many photos each) ──────────────────────────────── */}
            {index && slug && tab === 'places' && (
              <>
                {selPlace && (
                  <div className="ixp-detail">
                    <div className="ixp-dname">{iconFor(selPlace.kind)} {selPlace.name}</div>
                    <div className="ixp-dsub">{selPlace.sub || KIND_LABEL[selPlace.kind] || selPlace.kind} · Floor {floorTag(selPlace.floor)}</div>
                    <button className="ixp-idchip" onClick={() => copyId(selPlace.id)} title="Copy place id">{selPlace.id} ⧉</button>
                    <div className="ixp-panel-row">
                      <button className="ixp-primary sm" onClick={() => addPlacePhoto(selPlace)}>＋ Add photo</button>
                      <button className="ixp-btn" onClick={() => panTo(selPlace.lat, selPlace.lng)}>Centre</button>
                    </div>
                    <ol className="ixp-order-list">
                      {(shotsById.get(selPlace.id) || []).map((idx, k) => (
                        <li key={idx}>
                          <span className="ixp-oi">{k + 1}</span>
                          <span className="ixp-on">Photo #{idx + 1} in this run</span>
                          <span className="ixp-ob">{shortTime(session.entries[idx].at)}</span>
                          <button className="ixp-btn danger sm" onClick={() => removeShot(idx)}>✕</button>
                        </li>
                      ))}
                      {!(shotsById.get(selPlace.id) || []).length && <li className="ixp-noshots">No photos yet — tap the place on the map or ＋ Add photo.</li>}
                    </ol>
                  </div>
                )}
                {placeRows.length === 0 && <div className="ixp-empty">No places on this floor.</div>}
                {placeRows.map(p => {
                  const shots = shotsById.get(p.id) || [];
                  return (
                    <div key={p.id} className={`ixp-row ${p.id === selId ? 'sel' : ''} ${shots.length ? 'done' : ''}`}>
                      <button className="ixp-rowmain" onClick={() => { setSelId(p.id); panTo(p.lat, p.lng); }}>
                        <span className="ixp-chip" style={{ background: `${colorFor(p.kind)}1f`, borderColor: `${colorFor(p.kind)}55` }}>
                          {iconFor(p.kind)}
                        </span>
                        <span className="ixp-rowtext">
                          <span className="ixp-rowname">{p.name}</span>
                          <span className="ixp-rowsub">{p.sub || KIND_LABEL[p.kind] || p.kind}</span>
                        </span>
                      </button>
                      {shots.length > 0 && <span className="ixp-order">{shots.length} 📷</span>}
                      <button className="ixp-done on" onClick={() => addPlacePhoto(p)}>＋</button>
                    </div>
                  );
                })}
              </>
            )}

            {/* ── Shots in this run ──────────────────────────────────────── */}
            {index && tab === 'shots' && (
              <>
                {session.entries.length === 0 && <div className="ixp-empty">Nothing shot yet.</div>}
                {session.entries.map((e, i) => (
                  <div key={e.targetId + '_' + i} className="ixp-row">
                    <button className="ixp-rowmain" onClick={() => {
                      if (e.slug && e.slug !== slug) pickBuilding(e.slug);
                      setFloor(e.floor); setSelId(e.targetId);
                      setTab(entryType(e) === 'place' ? 'places' : 'nodes');
                      panTo(e.lat, e.lng);
                    }}>
                      <span className="ixp-chip">{i + 1}</span>
                      <span className="ixp-rowtext">
                        <span className="ixp-rowname">{iconFor(e.kind)} {e.name}</span>
                        <span className="ixp-rowsub">{nameOf(e.slug)} · F{floorTag(e.floor)} · {shortTime(e.at)}</span>
                      </span>
                    </button>
                    <button className="ixp-done danger" onClick={() => removeShot(i)}>✕</button>
                  </div>
                ))}
              </>
            )}
            <div className="ixp-foot">
              Nodes and places come from UMapsTools — re-run scripts/export-phototracker-web.py after editing them.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const CSS = `
.ixp-login { min-height: 100vh; background: #f1f3f4; display: flex; align-items: center; justify-content: center;
  font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; padding: 16px; box-sizing: border-box; }
.ixp-card { width: 100%; max-width: 340px; background: #fff; padding: 24px; border-radius: 16px;
  box-shadow: 0 6px 28px rgba(0,0,0,0.14); display: flex; flex-direction: column; }
.ixp-h1 { font-size: 22px; font-weight: 800; color: #202124; }
.ixp-muted { color: #5f6368; font-size: 14px; margin: 6px 0 12px; }
.ixp-input { font-size: 16px; padding: 11px 13px; border: 1.5px solid #dadce0; border-radius: 10px; outline: none; margin-bottom: 12px; }
.ixp-primary { background: #1a73e8; color: #fff; border: none; border-radius: 10px; padding: 12px; font-size: 15px; font-weight: 800; cursor: pointer; }
.ixp-primary.sm { padding: 9px 16px; font-size: 14px; }
.ixp-primary.undo { background: #137333; }
.ixp-primary:disabled { opacity: .6; }
.ixp-err { background: #fce8e6; color: #c5221f; border-radius: 8px; padding: 8px 11px; font-size: 13px; margin-bottom: 12px; }
.ixp-err.ixp-inline { margin: 8px 12px; }

.ixp-page { display: flex; flex-direction: column; height: 100vh; height: 100dvh; background: #f1f3f4;
  font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; color: #202124; }
.ixp-head { display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #fff;
  border-bottom: 1px solid #e0e0e0; flex: none; }
.ixp-title { font-weight: 800; font-size: 15px; white-space: nowrap; }
.ixp-count { background: #e8f0fe; color: #1a73e8; border-radius: 999px; padding: 3px 10px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.ixp-spacer { flex: 1; }
.ixp-btn { background: #fff; color: #202124; border: 1.5px solid #dadce0; border-radius: 8px;
  padding: 7px 10px; font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.ixp-btn:disabled { opacity: .4; }
.ixp-btn.on { background: #1a73e8; border-color: #1a73e8; color: #fff; }
.ixp-btn.warn { background: #fef7e0; border-color: #f9ab00; color: #b06000; }
.ixp-btn.danger { color: #c5221f; }
.ixp-btn.sm { padding: 4px 8px; font-size: 12px; }

.ixp-pickbar { display: flex; gap: 8px; align-items: center; padding: 8px 12px; background: #fff;
  border-bottom: 1px solid #e0e0e0; flex: none; }
.ixp-select { flex: 1; min-width: 0; font-size: 15px; font-weight: 700; padding: 9px 10px;
  border: 1.5px solid #dadce0; border-radius: 10px; background: #fff; color: #202124; }
.ixp-floors { display: flex; gap: 4px; overflow-x: auto; -webkit-overflow-scrolling: touch; }

.ixp-map-wrap { position: relative; flex: none; height: 42vh; }
.ixp-map { position: absolute; inset: 0; }
/* Ring sits above the map canvas but below the controls. */
.ixp-map-wrap.active::after { content: ''; position: absolute; inset: 0; z-index: 1;
  pointer-events: none; box-shadow: inset 0 0 0 3px #34a853; }
.ixp-map-wrap.active .ixp-map { cursor: crosshair; }
.ixp-mapctrls { position: absolute; z-index: 2; top: 8px; left: 8px; right: 8px; display: flex; gap: 5px; align-items: center;
  flex-wrap: wrap; background: rgba(255,255,255,0.92); border-radius: 10px; padding: 6px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
.ixp-shown { font-size: 12px; font-weight: 700; margin-right: 4px; }
.ixp-floor { background: #fff; border: 1.5px solid #dadce0; border-radius: 8px; min-width: 34px;
  padding: 7px 9px; font-size: 13px; font-weight: 800; cursor: pointer; }
.ixp-floor.on { background: #1a73e8; border-color: #1a73e8; color: #fff; }
.ixp-active { margin-left: auto; background: #fff; border: 1.5px solid #dadce0; border-radius: 8px;
  padding: 5px 10px; font-size: 12px; font-weight: 800; cursor: pointer; white-space: nowrap; }
.ixp-active.on { background: #34a853; border-color: #34a853; color: #fff; }
.ixp-maplabel { text-shadow: 0 0 3px rgba(255,255,255,.95), 0 1px 2px rgba(255,255,255,.95); transform: translateY(18px); }

.ixp-listwrap { flex: 1; min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch; padding-bottom: 24px; }
.ixp-tabs { display: flex; gap: 6px; padding: 10px 12px 0; position: sticky; top: 0; background: #f1f3f4; z-index: 3; }
.ixp-tab { flex: 1; background: #fff; border: 1.5px solid #dadce0; border-radius: 10px;
  padding: 9px 6px; font-size: 13px; font-weight: 800; cursor: pointer; white-space: nowrap; }
.ixp-tab.on { background: #1a73e8; border-color: #1a73e8; color: #fff; }
.ixp-search { width: calc(100% - 24px); margin: 10px 12px; padding: 11px 13px; font-size: 16px;
  border: 1.5px solid #dadce0; border-radius: 10px; outline: none; box-sizing: border-box; }
.ixp-kinds { display: flex; gap: 6px; overflow-x: auto; padding: 0 12px 10px; -webkit-overflow-scrolling: touch; }
.ixp-kind { flex: none; background: #fff; border: 1.5px solid #dadce0; border-radius: 999px;
  padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap; opacity: .5; }
.ixp-kind.on { background: #1a73e8; border-color: #1a73e8; color: #fff; opacity: 1; }
.ixp-note { margin: 8px 12px; padding: 8px 11px; background: #e6f4ea; color: #137333; border-radius: 8px; font-size: 13px; }
.ixp-empty { padding: 16px; color: #5f6368; font-size: 14px; }

.ixp-detail { margin: 0 12px 10px; padding: 12px; background: #fff; border-radius: 12px;
  border: 1.5px solid #ffca28; box-shadow: 0 2px 10px rgba(0,0,0,.07); }
.ixp-dname { font-size: 15px; font-weight: 800; margin-bottom: 2px; }
.ixp-dsub { font-size: 12px; color: #5f6368; margin-bottom: 8px; }
.ixp-idchip { display: block; width: 100%; text-align: left; font-family: ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size: 11px; color: #5f6368; background: #f1f3f4; border: 1px solid #e0e0e0; border-radius: 6px;
  padding: 5px 7px; margin-bottom: 10px; cursor: pointer; overflow: hidden; text-overflow: ellipsis; }
.ixp-noshots { color: #5f6368; font-size: 12px; }

.ixp-grouphead { width: 100%; display: flex; align-items: center; gap: 8px; padding: 10px 12px;
  background: #e8eaed; border: none; border-bottom: 1px solid #dadce0; cursor: pointer; text-align: left;
  font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #5f6368; }
.ixp-groupname { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ixp-groupn { margin-left: auto; opacity: .8; }
.ixp-caret { font-size: 8px; transition: transform .15s; display: inline-block; }
.ixp-caret.open { transform: rotate(90deg); }
.ixp-row { display: flex; align-items: center; gap: 6px; padding: 0 10px 0 0; background: #fff;
  border-bottom: 1px solid #f1f3f4; }
.ixp-row.sel { background: #fef7e0; box-shadow: inset 3px 0 0 #ffca28; }
.ixp-row.done .ixp-rowname { color: #5f6368; }
.ixp-rowmain { flex: 1; min-width: 0; display: flex; align-items: center; gap: 10px;
  padding: 12px 8px 12px 12px; background: none; border: none; cursor: pointer; text-align: left; }
.ixp-chip { flex: none; min-width: 44px; text-align: center; color: #202124;
  border: 1px solid #dadce0; border-radius: 6px; padding: 4px 6px; font-size: 12px; font-weight: 800; }
.ixp-row.done .ixp-chip { background: #e8eaed !important; color: #5f6368; border-color: #dadce0 !important; }
.ixp-rowtext { display: flex; flex-direction: column; min-width: 0; }
.ixp-rowname { font-size: 14px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ixp-rowsub { font-size: 12px; color: #5f6368; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ixp-order { font-size: 12px; font-weight: 800; color: #137333; flex: none; }
.ixp-done { flex: none; min-width: 62px; background: #fff; border: 1.5px solid #dadce0; border-radius: 8px;
  padding: 9px 10px; font-size: 13px; font-weight: 700; cursor: pointer; }
.ixp-done.on { background: #e6f4ea; border-color: #34a853; color: #137333; }
.ixp-done.danger { min-width: 44px; color: #c5221f; }
.ixp-foot { padding: 14px 12px; color: #5f6368; font-size: 12px; }

.ixp-dash { flex: 1; min-height: 0; overflow-y: auto; padding: 12px; -webkit-overflow-scrolling: touch; }
.ixp-panel { background: #fff; border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,.08); padding: 14px; margin-bottom: 12px; }
.ixp-panel-h { font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #5f6368; margin-bottom: 10px; }
.ixp-storage { text-transform: none; letter-spacing: 0; font-weight: 600; color: #9aa0a6; margin-left: 6px; }
.ixp-namein { width: 100%; box-sizing: border-box; margin-bottom: 6px; font-weight: 700; }
.ixp-panel-sub { color: #5f6368; font-size: 13px; margin-bottom: 12px; }
.ixp-panel-row { display: flex; gap: 8px; flex-wrap: wrap; }
.ixp-order-list { list-style: none; margin: 14px 0 0; padding: 0; border-top: 1px solid #f1f3f4; }
.ixp-order-list li { display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f1f3f4; font-size: 13px; }
.ixp-oi { flex: none; min-width: 26px; height: 26px; border-radius: 999px; background: #e6f4ea; color: #137333;
  font-weight: 800; display: flex; align-items: center; justify-content: center; font-size: 12px; }
.ixp-on { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.ixp-ob { color: #5f6368; font-size: 12px; white-space: nowrap; }
.ixp-srow { display: flex; align-items: center; gap: 8px; padding: 10px 0; border-bottom: 1px solid #f1f3f4; }
.ixp-stext { flex: 1; min-width: 0; }
.ixp-sname { font-weight: 700; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ixp-ssub { color: #5f6368; font-size: 12px; }

@media (min-width: 860px) {
  .ixp-page:not(.dash) { display: grid; grid-template-columns: 420px 1fr; grid-template-rows: auto auto 1fr; height: 100vh; }
  .ixp-page:not(.dash) .ixp-head { grid-column: 1 / -1; }
  .ixp-page:not(.dash) .ixp-pickbar { grid-column: 1 / -1; }
  .ixp-page:not(.dash) .ixp-listwrap { grid-column: 1; grid-row: 3; border-right: 1px solid #e0e0e0; background: #fff; }
  .ixp-page:not(.dash) .ixp-map-wrap { grid-column: 2; grid-row: 3; height: auto; }
  .ixp-page:not(.dash) .ixp-tabs { background: #fff; }
  .ixp-dash { max-width: 760px; margin: 0 auto; width: 100%; box-sizing: border-box; }
}
`;
